/**
 * リスク表示定数
 * 気象リスクの種類、レベル、表示設定を定義
 */
import { type RiskGrade, getRiskGrade } from '../utils/riskGrade';

/** リスク種類の定数マップ */
export const RISK_TYPES = {
  FROST: 'frost',
  WIND: 'wind',
  HEAVY_RAIN: 'heavy_rain',
  CRUST: 'crust',
  DIV: 'div',
} as const;

/** リスク種類の型（RISK_TYPES の値から導出） */
export type RiskType = (typeof RISK_TYPES)[keyof typeof RISK_TYPES];

/**
 * リスクタイプに対応する Material Symbols アイコン名
 */
export const RISK_ICONS: Record<RiskType, string> = {
  [RISK_TYPES.FROST]: 'ac_unit',
  [RISK_TYPES.WIND]: 'air',
  [RISK_TYPES.HEAVY_RAIN]: 'rainy_heavy',
  [RISK_TYPES.CRUST]: 'sound_detection_glass_break',
  [RISK_TYPES.DIV]: 'microbiology',
};

/** リスク表示設定のインターフェース */
export interface RiskDisplayConfig {
  icon: string; // Material Design Icon名
  highRiskColor: string; // 高リスク時の色
  lowRiskColor: string; // 低リスク時の色（不透明度30%）
  label: string; // リスク種類のラベル
}

/** リスク種類ごとの表示設定マップ */
export const RISK_DISPLAY_CONFIG: Record<string, RiskDisplayConfig> = {
  [RISK_TYPES.FROST]: {
    icon: RISK_ICONS[RISK_TYPES.FROST],
    highRiskColor: '#9345B8',
    lowRiskColor: '#9345B830',
    label: '霜害',
  },
  [RISK_TYPES.WIND]: {
    icon: RISK_ICONS[RISK_TYPES.WIND],
    highRiskColor: '#948D77',
    lowRiskColor: '#948D7730',
    label: '風害',
  },
  [RISK_TYPES.HEAVY_RAIN]: {
    icon: RISK_ICONS[RISK_TYPES.HEAVY_RAIN],
    highRiskColor: '#158DC4',
    lowRiskColor: '#158DC430',
    label: '大雨',
  },
  [RISK_TYPES.CRUST]: {
    icon: RISK_ICONS[RISK_TYPES.CRUST],
    highRiskColor: '#92622B',
    lowRiskColor: '#92622B30',
    label: 'クラスト害',
  },
  [RISK_TYPES.DIV]: {
    icon: RISK_ICONS[RISK_TYPES.DIV],
    highRiskColor: '#B8471D',
    lowRiskColor: '#B8471D30',
    label: '褐斑病',
  },
};

/**
 * リスク表示プロパティ
 * getRiskDisplayProps() の戻り値として、表示に必要な全情報をまとめる。
 */
export interface RiskDisplayResult {
  /** セマンティックなリスク等級 */
  grade: RiskGrade;
  /** アイコンを表示すべきか（caution/warning = true, none = false） */
  visible: boolean;
  /** アイコン背景色（warning: 濃色, caution: 薄色） */
  backgroundColor: string;
  /** Material Symbols アイコン名 */
  icon: string;
}

/**
 * リスクタイプと生値から、表示に必要な全プロパティを一括導出する。
 * すべてのカレンダー・インジケーターコンポーネントはこの関数を使い、
 * リスク表示の可否・色・アイコンを統一的に決定する。
 *
 * | 等級      | visible | backgroundColor   |
 * |-----------|---------|-------------------|
 * | warning   | true    | highRiskColor     |
 * | caution   | true    | lowRiskColor      |
 * | none      | false   | transparent       |
 */
export function getRiskDisplayProps(riskType: string, riskLevel: number): RiskDisplayResult {
  const grade = getRiskGrade(riskType, riskLevel);
  const config = RISK_DISPLAY_CONFIG[riskType];
  const icon = config?.icon || 'help_outline';

  switch (grade) {
    case 'warning':
      return { grade, visible: true, backgroundColor: config?.highRiskColor || '#999999', icon };
    case 'caution':
      return { grade, visible: true, backgroundColor: config?.lowRiskColor || '#99999930', icon };
    case 'none':
    default:
      return { grade: 'none', visible: false, backgroundColor: 'transparent', icon };
  }
}

/**
 * HARC API 生リスク値に応じた色を取得するヘルパー関数
 * 内部で getRiskGrade() を使い、リスク等級に応じた色を返す。
 *
 * 3段階カラーマッピング:
 * - warning → highRiskColor（濃色）
 * - caution → lowRiskColor（薄色: 不透明度30%）
 * - none    → transparent
 *
 * @param riskType リスク種類
 * @param riskLevel HARC API の生リスク値
 * @returns 色コード
 */
export function getRiskColor(riskType: string, riskLevel: number): string {
  const config = RISK_DISPLAY_CONFIG[riskType];
  if (!config) {
    return '#999999'; // デフォルト色
  }
  const grade = getRiskGrade(riskType, riskLevel);
  switch (grade) {
    case 'warning':
      return config.highRiskColor;
    case 'caution':
      return config.lowRiskColor;
    case 'none':
    default:
      return 'transparent';
  }
}

/**
 * リスク種類に応じたアイコン名を取得するヘルパー関数
 * @param riskType リスク種類
 * @returns Material Design Iconの名前
 */
export function getRiskIcon(riskType: string): string {
  const config = RISK_DISPLAY_CONFIG[riskType];
  return config?.icon || 'help_outline';
}

/**
 * リスクタイプの表示順序
 * カレンダー等で上から下への表示順を定義
 * クラスト害 → 霜害 → 風害 → 大雨
 */
/**
 * リスク情報A（BEFORE_SEEDING / BEFORE_EMERGENCE / INITIAL_GROWTH）の表示順
 */
export const RISK_TYPE_ORDER: Record<string, number> = {
  [RISK_TYPES.CRUST]: 1,
  [RISK_TYPES.FROST]: 2,
  [RISK_TYPES.WIND]: 3,
  [RISK_TYPES.HEAVY_RAIN]: 4,
} as const;

/**
 * リスク情報B（PEST_CONTROL）の表示順
 */
export const RISK_TYPE_ORDER_B: Record<string, number> = {
  [RISK_TYPES.DIV]: 1,
  [RISK_TYPES.WIND]: 2,
  [RISK_TYPES.HEAVY_RAIN]: 3,
} as const;

/**
 * リスク配列をタイプ順でソートするヘルパー関数
 * @param risks リスク配列
 * @param orderMap ソート順マップ（省略時は RISK_TYPE_ORDER を使用）
 * @returns ソート済みリスク配列（元の配列は変更しない）
 */
export function sortRisksByType<T extends { riskType: string }>(
  risks: T[],
  orderMap?: Record<string, number>
): T[] {
  const order = orderMap ?? RISK_TYPE_ORDER;
  return [...risks].sort((a, b) => {
    const orderA = order[a.riskType] || 999;
    const orderB = order[b.riskType] || 999;
    return orderA - orderB;
  });
}
